import React, { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Link2Off, AlignLeft, AlignCenter, Palette, Eraser } from "lucide-react";

const exec = (cmd, val = null) => document.execCommand(cmd, false, val);

// Dependency-free WYSIWYG editor (contentEditable + execCommand). Stores HTML.
export default function RichTextEditor({ value, onChange, placeholder = "", testId }) {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const lastEmitted = useRef("");
  const emitTimer = useRef(null);

  // Only reset the DOM when `value` changes EXTERNALLY (e.g. language switch or
  // initial load), never from our own edits — otherwise a parent re-render on
  // every keystroke (heavy pages with live preview) would wipe what the user types.
  useEffect(() => {
    if (!ref.current) return;
    const v = value || "";
    if (v !== lastEmitted.current) {
      if (ref.current.innerHTML !== v) ref.current.innerHTML = v;
      lastEmitted.current = v;
    }
  }, [value]);

  useEffect(() => () => { if (emitTimer.current) clearTimeout(emitTimer.current); }, []);

  // Push the current HTML to the parent NOW.
  const flush = () => {
    if (emitTimer.current) { clearTimeout(emitTimer.current); emitTimer.current = null; }
    const html = ref.current ? ref.current.innerHTML : "";
    lastEmitted.current = html;
    onChange(html);
  };
  // While typing, debounce the parent update so the heavy page (live preview)
  // doesn't re-render on every keystroke and steal focus/caret from the editor.
  const emit = () => {
    const html = ref.current ? ref.current.innerHTML : "";
    lastEmitted.current = html; // mark immediately so the external effect won't reset us
    if (emitTimer.current) clearTimeout(emitTimer.current);
    emitTimer.current = setTimeout(() => { emitTimer.current = null; onChange(html); }, 350);
  };
  const run = (cmd, val) => { ref.current?.focus(); exec(cmd, val); flush(); };

  const saveSel = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0);
    }
  };
  const restoreSel = () => {
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };
  // Apply an exact pixel font-size to the current selection (execCommand only
  // supports presets 1-7, so we tag then convert to an inline style).
  const applyPx = (px) => {
    if (!ref.current || !px) return;
    ref.current.focus();
    restoreSel();
    exec("fontSize", "7");
    ref.current.querySelectorAll('font[size="7"]').forEach((f) => {
      f.removeAttribute("size");
      f.style.fontSize = `${px}px`;
    });
    flush();
  };

  const Btn = ({ onClick, title, children }) => (
    <button type="button" title={title}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-[#0B1D3A]/10 text-[#0B1D3A]/70">
      {children}
    </button>
  );

  return (
    <div className="rounded-lg border border-[#0B1D3A]/15 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-[#0B1D3A]/10 bg-[#0B1D3A]/[0.03]">
        <Btn onClick={() => run("bold")} title="Negrito"><Bold size={14} /></Btn>
        <Btn onClick={() => run("italic")} title="Itálico"><Italic size={14} /></Btn>
        <Btn onClick={() => run("underline")} title="Sublinhado"><Underline size={14} /></Btn>
        <select title="Tamanho" onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { run("fontSize", e.target.value); e.target.selectedIndex = 0; }}
          className="h-7 text-xs rounded border border-[#0B1D3A]/10 bg-white px-1 text-[#0B1D3A]/70">
          <option value="">Tam.</option>
          <option value="1">Muito pequeno</option>
          <option value="2">Pequeno</option>
          <option value="3">Normal</option>
          <option value="5">Grande</option>
          <option value="6">Muito grande</option>
          <option value="7">Enorme</option>
        </select>
        <input type="number" min="8" max="120" placeholder="px" title="Tamanho exato em px (seleciona o texto primeiro, depois escreve o valor e Enter)"
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPx(parseInt(e.target.value, 10)); } }}
          onBlur={(e) => { applyPx(parseInt(e.target.value, 10)); }}
          className="h-7 w-14 text-xs rounded border border-[#0B1D3A]/10 bg-white px-1.5 text-[#0B1D3A]/70" />
        <label className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-[#0B1D3A]/10 cursor-pointer text-[#0B1D3A]/70 relative" title="Cor do texto">
          <Palette size={14} />
          <input type="color" onChange={(e) => run("foreColor", e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer" />
        </label>
        <Btn onClick={() => run("insertUnorderedList")} title="Lista com marcadores"><List size={14} /></Btn>
        <Btn onClick={() => run("insertOrderedList")} title="Lista numerada"><ListOrdered size={14} /></Btn>
        <Btn onClick={() => {
          const sel = window.getSelection();
          const existing = sel && sel.anchorNode?.parentElement?.closest("a");
          const defaultUrl = existing?.getAttribute("href") || "https://";
          const text = sel && sel.toString();
          let url = window.prompt("URL do link (abre em nova janela):", defaultUrl);
          if (!url) return;
          url = url.trim();
          if (!/^(https?:|mailto:|tel:|#|\/)/.test(url)) url = "https://" + url;
          // The toolbar's onMouseDown already saved the selection; restore it
          // before issuing the command (prompt() loses the active selection).
          restoreSel();
          if (!text && !existing) {
            // No selection: insert the URL text as the link label.
            document.execCommand("insertHTML", false,
              `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
          } else {
            document.execCommand("createLink", false, url);
          }
          // Force target=_blank on any newly-created or edited <a>.
          const root = ref.current;
          if (root) root.querySelectorAll("a[href]").forEach((a) => {
            if (a.getAttribute("href") === url) {
              a.setAttribute("target", "_blank");
              a.setAttribute("rel", "noopener noreferrer");
            }
          });
          emit();
        }} title="Inserir / editar link"><Link2 size={14} /></Btn>
        <Btn onClick={() => run("unlink")} title="Remover link"><Link2Off size={14} /></Btn>
        <Btn onClick={() => run("justifyLeft")} title="Alinhar à esquerda"><AlignLeft size={14} /></Btn>
        <Btn onClick={() => run("justifyCenter")} title="Centrar"><AlignCenter size={14} /></Btn>
        <Btn onClick={() => run("removeFormat")} title="Limpar formatação"><Eraser size={14} /></Btn>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning data-testid={testId}
        onInput={emit} onBlur={flush} onMouseUp={saveSel} onKeyUp={saveSel} data-ph={placeholder}
        className="min-h-[56px] px-3 py-2 text-sm outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-blue-600 [&_a]:underline empty:before:content-[attr(data-ph)] empty:before:text-[#0B1D3A]/30" />
    </div>
  );
}
